import { useState, useMemo, memo } from 'react';
import { format } from 'date-fns';
import { useAuthStore } from '@/store/auth.store';
import { useMessagingStore } from '@/store/messaging.store';
import { useMessagingSocket } from '@/hooks/useMessagingSocket';
import type { MessageWithDetails, MessageAttachment, MentionData } from '@/types/messaging';
import { safeHref } from '@/utils/safe-url';

import ConfirmDialog from '../common/ConfirmDialog';
import FaIcon from '../common/FaIcon';
import Button, { IconButton } from '../common/Button';
import Input from '../common/Input';
// URL detection regex
const URL_REGEX = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/;
// Mention regex - matches @Name patterns (Unicode-aware for non-ASCII names)
const MENTION_REGEX = /@([\p{L}\p{N}_][\p{L}\p{N}_\s]*?)(?=\s@|\s|$)/gu;

interface MessageItemProps {
  message: MessageWithDetails;
  showSender: boolean;
  conversationId: string;
  onForward?: (message: MessageWithDetails) => void;
}

export const MessageItem = memo(function MessageItem({ message, showSender, conversationId, onForward }: MessageItemProps) {
  const { user } = useAuthStore();
  const { setReplyingTo, addPinnedMessage, removePinnedMessage, pinnedMessages } = useMessagingStore();
  const { toggleReaction, deleteMessage, editMessage } = useMessagingSocket();
  const [showActions, setShowActions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isOwnMessage = message.sender_id === user?.id;
  const isSystemMessage = message.type === 'system';
  const isPinned = useMemo(
    () => pinnedMessages[conversationId]?.some((m: MessageWithDetails) => m.id === message.id) ?? false,
    [pinnedMessages, conversationId, message.id]
  );

  // Check delivery/read status
  const isRead = (message.read_by ?? []).length > 0;
  const deliveredTo = (message.metadata?.delivered_to as string[] | undefined) ?? [];
  const isDelivered = !isRead && deliveredTo.length > 0;

  const handleReaction = (emoji: string) => {
    toggleReaction({
      message_id: message.id,
      conversation_id: conversationId,
      emoji,
    });
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const executeDelete = () => {
    deleteMessage({
      message_id: message.id,
      conversation_id: conversationId,
    });
    setShowDeleteConfirm(false);
  };

  const handleReply = () => {
    setReplyingTo(conversationId, message);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditContent(message.content || '');
  };

  const handleSaveEdit = () => {
    if (editContent.trim() && editContent !== message.content) {
      editMessage({
        message_id: message.id,
        conversation_id: conversationId,
        content: editContent.trim(),
      });
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(message.content || '');
  };

  const handleForward = () => {
    if (onForward) {
      onForward(message);
    }
  };

  const handlePin = () => {
    if (isPinned) {
      removePinnedMessage(conversationId, message.id);
    } else {
      addPinnedMessage(conversationId, message);
    }
  };

  // Memoized timestamp formatting
  const formattedTime = useMemo(
    () => format(new Date(message.created_at), 'h:mm a'),
    [message.created_at]
  );

  // Build a set of mentioned names for highlighting
  const mentionNames = useMemo(() => {
    const mentions = (message.metadata as { mentions?: MentionData[] })?.mentions;
    if (!mentions || mentions.length === 0) return new Set<string>();
    return new Set(mentions.map(m => m.name));
  }, [message.metadata]);

  // Render a text segment with @mention highlighting
  const renderWithMentions = (text: string, keyPrefix: string) => {
    if (mentionNames.size === 0) return text;
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    MENTION_REGEX.lastIndex = 0;
    let match;
    while ((match = MENTION_REGEX.exec(text)) !== null) {
      const name = match[1].trim();
      if (mentionNames.has(name)) {
        if (match.index > lastIndex) {
          result.push(text.slice(lastIndex, match.index));
        }
        result.push(
          <span
            key={`${keyPrefix}-mention-${match.index}`}
            className={`font-semibold ${
              isOwnMessage ? 'text-primary-100' : 'text-primary-600 dark:text-primary-400'
            }`}
          >
            @{name}
          </span>
        );
        lastIndex = match.index + match[0].length;
      }
    }
    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex));
    }
    return result.length > 0 ? result : text;
  };

  // Memoized content rendering with links and mentions
  const renderedContent = useMemo(() => {
    const content = message.content || '';
    if (!content) return null;
    const parts = content.split(URL_REGEX);
    return parts.map((part, index) => {
      if (part && URL_REGEX.test(part)) {
        return (
          <a
            key={index}
            href={safeHref(part)}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline hover:no-underline ${
              isOwnMessage ? 'text-primary-100' : 'text-primary-500 dark:text-primary-400'
            }`}
          >
            {part}
          </a>
        );
      }
      return renderWithMentions(part, `part-${index}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.content, isOwnMessage, mentionNames]);

  // Memoized reactions aggregation
  const aggregatedReactions = useMemo(() => {
    if (!message.reactions || message.reactions.length === 0) return [];
    const map = new Map<string, { count: number; hasReacted: boolean }>();
    const userId = user?.id;

    for (const reaction of message.reactions) {
      const existing = map.get(reaction.emoji) || { count: 0, hasReacted: false };
      existing.count++;
      if (reaction.user_id === userId) {
        existing.hasReacted = true;
      }
      map.set(reaction.emoji, existing);
    }

    return Array.from(map.entries()).map(([emoji, data]) => ({
      emoji,
      ...data,
    }));
  }, [message.reactions, user?.id]);

  if (isSystemMessage) {
    return (
      <div className="flex justify-center my-2">
        <span className="text-xs text-theme-tertiary italic">
          {message.sender?.name || 'System'} {message.content}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`group flex ${isOwnMessage ? 'justify-end' : 'justify-start'} ${showSender ? 'mt-3' : 'mt-0.5'}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className={`max-w-[80%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
        {/* Sender name */}
        {showSender && !isOwnMessage && (
          <p className="text-xs text-theme-tertiary mb-1 ml-1">
            {message.sender?.name || 'Unknown'}
          </p>
        )}

        {/* Pinned indicator */}
        {isPinned && (
          <div className={`flex items-center gap-1 text-xs text-amber-500 mb-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
            <FaIcon icon="thumbtack" className="w-4 h-4" />
            <span>Pinned</span>
          </div>
        )}

        <div className="flex items-end gap-1">
          {/* Actions (left side for own messages) */}
          {isOwnMessage && showActions && !isEditing && (
            <MessageActions
              onDelete={handleDelete}
              onReply={handleReply}
              onEdit={handleEdit}
              onForward={onForward ? handleForward : undefined}
              onPin={handlePin}
              isPinned={isPinned}
            />
          )}

          {/* Message bubble */}
          <div
            className={`relative px-3 py-2 rounded-2xl ${
              isOwnMessage
                ? 'bg-primary-500 text-white rounded-br-md'
                : 'bg-surface-tertiary text-theme-primary rounded-bl-md'
            }`}
          >
            {/* Reply indicator */}
            {message.reply_to && (
              <div
                className={`text-xs mb-1 pb-1 border-b cursor-pointer hover:opacity-80 ${
                  isOwnMessage
                    ? 'border-primary-400 text-primary-100'
                    : 'border-gray-200 dark:border-gray-600 text-theme-tertiary'
                }`}
              >
                <span className="font-medium">
                  {message.reply_to.sender?.name || 'Unknown'}
                </span>
                <p className="truncate max-w-[200px]">{message.reply_to.content}</p>
              </div>
            )}

            {/* Content or Edit mode */}
            {isEditing ? (
              <div className="space-y-2">
                <Input aria-label="Edit message"
                  as="textarea"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="px-2 py-1 text-sm resize-none"
                  rows={3}
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelEdit}
                    className="px-2 py-1 text-xs"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveEdit}
                    className="px-2 py-1 text-xs"
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : message.metadata?.gif ? (
              // Render GIF
              <div className="max-w-[280px]">
                <img
                  src={message.metadata.gif.url || message.metadata.gif.preview_url}
                  alt={message.metadata.gif.title || 'GIF'}
                  className="rounded-lg max-w-full"
                  loading="lazy"
                />
              </div>
            ) : message.type === 'file' && message.attachments && message.attachments.length > 0 ? null : (
              <p className="text-sm whitespace-pre-wrap break-words">
                {renderedContent}
              </p>
            )}

            {/* Attachments */}
            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {message.attachments.map((attachment) => (
                  <AttachmentPreview key={attachment.id} attachment={attachment} />
                ))}
              </div>
            )}

            {/* Time, edited indicator, and delivery status */}
            <div
              className={`flex items-center gap-1 mt-1 text-xs ${
                isOwnMessage ? 'text-primary-200' : 'text-theme-tertiary'
              }`}
            >
              <span>{formattedTime}</span>
              {message.is_edited && <span>(edited)</span>}

              {/* Delivery status for own messages */}
              {isOwnMessage && (
                <span className="ml-1">
                  {isRead ? (
                    // Double check - read
                    <FaIcon icon="check-double" className="w-4 h-4 text-primary-200" />
                  ) : isDelivered ? (
                    // Single check - delivered (muted)
                    <FaIcon icon="check" className="w-4 h-4 text-primary-300" />
                  ) : (
                    // Single check - sent (lighter)
                    <FaIcon icon="check" className="w-4 h-4 text-primary-400/60" />
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Actions (right side for others' messages) */}
          {!isOwnMessage && showActions && (
            <MessageActions
              onReply={handleReply}
              onForward={onForward ? handleForward : undefined}
              onPin={handlePin}
              isPinned={isPinned}
            />
          )}
        </div>

        {/* Reactions */}
        {aggregatedReactions.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
            {aggregatedReactions.map((reaction) => (
              <Button
                key={reaction.emoji}
                variant="ghost"
                size="sm"
                onClick={() => handleReaction(reaction.emoji)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs min-h-0 ${
                  reaction.hasReacted
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                    : 'bg-surface-tertiary text-gray-600 dark:text-gray-300'
                } hover:bg-gray-200 dark:hover:bg-gray-600`}
              >
                <span>{reaction.emoji}</span>
                <span>{reaction.count}</span>
              </Button>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={executeDelete}
        title="Delete Message"
        message={
          message.content?.trim()
            ? `Delete this message? "${message.content.trim().slice(0, 80)}${message.content.trim().length > 80 ? '…' : ''}" This action cannot be undone.`
            : 'Delete this message? This action cannot be undone.'
        }
        confirmLabel="Delete Message"
        variant="danger"
      />
    </div>
  );
});

interface MessageActionsProps {
  onDelete?: () => void;
  onReply: () => void;
  onEdit?: () => void;
  onForward?: () => void;
  onPin: () => void;
  isPinned: boolean;
}

function MessageActions({ onDelete, onReply, onEdit, onForward, onPin, isPinned }: MessageActionsProps) {
  return (
    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-surface-primary rounded-lg shadow-sm border border-theme-primary p-0.5">
      {/* Reply */}
      <IconButton
        icon={<FaIcon icon="reply" className="w-4 h-4" />}
        label="Reply"
        variant="ghost"
        size="sm"
        onClick={onReply}
      />

      {/* Edit (only for own messages) */}
      {onEdit && (
        <IconButton
          icon={<FaIcon icon="pen-to-square" className="w-4 h-4" />}
          label="Edit"
          variant="ghost"
          size="sm"
          onClick={onEdit}
        />
      )}

      {/* Forward */}
      {onForward && (
        <IconButton
          icon={<FaIcon icon="arrow-right" className="w-4 h-4" />}
          label="Forward"
          variant="ghost"
          size="sm"
          onClick={onForward}
        />
      )}

      {/* Pin */}
      <IconButton
        icon={<FaIcon icon="thumbtack" className="w-4 h-4" />}
        label={isPinned ? 'Unpin' : 'Pin'}
        variant="ghost"
        size="sm"
        onClick={onPin}
        className={isPinned ? 'text-amber-500' : ''}
      />

      {/* Delete (only for own messages) */}
      {onDelete && (
        <IconButton
          icon={<FaIcon icon="trash-can" className="w-4 h-4" />}
          label="Delete"
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="hover:text-red-500"
        />
      )}
    </div>
  );
}

interface AttachmentPreviewProps {
  attachment: MessageAttachment;
}

function AttachmentPreview({ attachment }: AttachmentPreviewProps) {
  const isImage = attachment.file_type.startsWith('image/');
  const isAudio = attachment.file_type.startsWith('audio/');
  const isVideo = attachment.file_type.startsWith('video/');

  if (isImage && attachment.url) {
    return (
      <a href={attachment.url} target="_blank" rel="noopener noreferrer">
        <img
          src={attachment.thumbnail_url || attachment.url}
          alt={attachment.file_name}
          className="max-w-full max-h-48 rounded-lg"
        />
      </a>
    );
  }

  if (isAudio && attachment.url) {
    return (
      <div className="flex items-center gap-2 p-2 bg-white/10 rounded-lg">
        <audio src={attachment.url} controls preload="metadata" className="h-10 max-w-full" />
      </div>
    );
  }

  if (isVideo && attachment.url) {
    return (
      <div className="p-2 bg-white/10 rounded-lg">
        <video src={attachment.url} controls preload="metadata" className="max-w-full max-h-64 rounded-lg" />
      </div>
    );
  }

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 p-2 bg-white/10 rounded-lg hover:bg-white/20"
    >
      <FaIcon icon="file-lines" className="w-5 h-5" />
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{attachment.file_name}</p>
        <p className="text-xs opacity-75">{formatFileSize(attachment.file_size)}</p>
      </div>
    </a>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
